import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  ReactElement,
} from 'react'
import { Link } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { debounce, isEqual as _isEqual } from 'lodash'
import moment from 'moment'

import { requestUsers, getUsers } from 'ducks/users/index'
import type { UserState, User } from 'ducks/users/index'

import Page from 'components/Shared/Page'

import { DEBOUNCE_TIME } from '../../constants'
import AdminUserOrganization from './AdminUserOrganization'
import './Admin.scss'

// Extend User type to include deletion status fields
interface UserItemProps extends User {
  deletedAt?: string
  marketingUnsubscribed?: boolean
}

const UserItem = ({ user }: { user: UserItemProps }): ReactElement => {
  // Check if user is deleted or anonymized based on common deletion patterns
  const isDeleted = !!user.deletedAt

  const isMarketingUnsubscribed = user.marketingUnsubscribed

  return (
    <Link className="admin-user-item" to={`/admin/users/${user.id}`}>
      <div className="user-id-cell">
        <p>{user.id}</p>
      </div>
      <div className="user-name-cell">
        <h5>{user.name}</h5>
        {isDeleted && (
          <span className="user-deleted-indicator">
            <span role="img" aria-label="deleted">
              🗑️
            </span>{' '}
            Deleted
          </span>
        )}
        {isMarketingUnsubscribed && !isDeleted && (
          <span className="user-marketing-unsubscribed-indicator">
            <span role="img" aria-label="email">
              📧
            </span>{' '}
            Unsubscribed
          </span>
        )}
      </div>

      <div className="user-email-cell">
        <p>{user.email}</p>
      </div>
      <div className="user-date-cell">
        <p>{moment(user.createdAt).format('LLL')}</p>
      </div>
      <div className="user-orgs-cell">
        {user.Organizations.map(org => (
          <AdminUserOrganization key={org.id} organization={org} />
        ))}
      </div>
    </Link>
  )
}

const AdminUsers = (): ReactElement => {
  const mounted = useRef<boolean>(false)
  const dispatch = useDispatch()
  const [value, setValue] = useState('')
  const users = useSelector(
    (state: UserState) => getUsers(state),
    _isEqual
  ) as UserItemProps[]

  const handleQuery = useCallback(
    debounce(() => {
      if (!value) {
        return dispatch(requestUsers())
      }

      return dispatch(requestUsers({ query: value }))
    }, DEBOUNCE_TIME),
    [value, dispatch]
  )

  useEffect(() => {
    if (!mounted.current) {
      dispatch(requestUsers())
      mounted.current = true
    } else {
      handleQuery()
    }
  }, [value, dispatch, handleQuery])

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    e.preventDefault()
    setValue(e.target.value)
  }

  return (
    <Page className="admin-users-page" title="Users">
      <section className="users-header">
        <h1>Users</h1>
        <input
          type="text"
          onChange={handleChange}
          value={value}
          placeholder="Search by name or email"
          autoFocus
        />
      </section>
      <div className="admin-users-header">
        <p>ID</p>
        <p>Name</p>
        <p>Email</p>
        <p>Signup Date</p>
        <p>Team(s)</p>
      </div>
      {users.map(u => (
        <UserItem key={u.id} user={u} />
      ))}
    </Page>
  )
}

export default AdminUsers
